import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
} from "@fluentui/react-components"
import type { AppKey, AppKeyPage, CreateAppKeyRequest } from "@rema/shared/schemas/app-keys"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { api, isApiError } from "../../lib/api"
import { problemMessage } from "../../lib/problem"
import { settingsI18n } from "./i18n"

const i18n = settingsI18n.apiAccess

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
  },
  form: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  grow: { flex: 1, minWidth: "200px" },
  mono: { fontFamily: tokens.fontFamilyMonospace },
})

const KEYS_KEY = ["admin", "app-keys"] as const
const parseOrigins = (raw: string) =>
  raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)

export const ApiAccessPanel = () => {
  const styles = useStyles()
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [origins, setOrigins] = useState("")
  const [error, setError] = useState<string | null>(null)

  const onError = (e: unknown) =>
    setError(isApiError(e) ? problemMessage(e.problem) : settingsI18n.common.saveError)
  const invalidate = () => qc.invalidateQueries({ queryKey: KEYS_KEY })

  const query = useQuery({
    queryKey: KEYS_KEY,
    queryFn: () => api.get<AppKeyPage>("/admin/app-keys"),
  })
  const create = useMutation({
    mutationFn: (input: CreateAppKeyRequest) => api.post<AppKey>("/admin/app-keys", input),
    onSuccess: () => {
      setError(null)
      setName("")
      setOrigins("")
      invalidate()
    },
    onError,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.post<AppKey>(`/admin/app-keys/${id}/revoke`),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError,
  })

  if (query.isPending) return <Spinner />
  const keys = query.data?.data ?? []
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.title}</Subtitle2>
      <p>{i18n.description}</p>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      <div className={styles.form}>
        <Field label={i18n.name} className={styles.grow}>
          <Input
            value={name}
            placeholder={i18n.namePlaceholder}
            onChange={(_, d) => setName(d.value)}
          />
        </Field>
        <Field label={i18n.origins} className={styles.grow}>
          <Input
            value={origins}
            placeholder="https://app.example"
            onChange={(_, d) => setOrigins(d.value)}
          />
        </Field>
        <Button
          appearance="primary"
          disabled={!name.trim() || create.isPending}
          onClick={() =>
            create.mutate({ name: name.trim(), allowed_origins: parseOrigins(origins) })
          }
        >
          {i18n.create}
        </Button>
      </div>
      {keys.length === 0 ? (
        <MessageBar>
          <MessageBarBody>{i18n.empty}</MessageBarBody>
        </MessageBar>
      ) : (
        <Table aria-label={i18n.title}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{i18n.columns.name}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.key}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.origins}</TableHeaderCell>
              <TableHeaderCell>{i18n.columns.status}</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.name}</TableCell>
                <TableCell className={styles.mono}>{key.key}</TableCell>
                <TableCell>{key.allowed_origins.join(", ") || "—"}</TableCell>
                <TableCell>
                  <Badge
                    appearance="filled"
                    color={key.status === "active" ? "success" : "informative"}
                  >
                    {i18n.statuses[key.status]}
                  </Badge>
                </TableCell>
                <TableCell>
                  {key.status === "active" ? (
                    <Button
                      appearance="subtle"
                      size="small"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(key.id)}
                    >
                      {i18n.revoke}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
